import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
} from '@mui/material';
import { Home, Add, Edit, Delete, Warehouse, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const empty = { storageCode: '', name: '', description: '' };
const API = process.env.REACT_APP_API_URL;

export default function StorageAreaList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [delDlg, setDelDlg] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: dialogOpen, value: form, onClose: () => setDialogOpen(false),
    message: 'You have unsaved storage area details. Close without saving?',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/library/storage-areas`, { headers }); setRows(data); setError(''); }
    catch (err) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r.storageCode); setForm({ ...empty, ...r }); setDialogOpen(true); };

  const save = async () => {
    if (!editing && !form.storageCode.trim()) return setError('Code is required');
    if (!form.name.trim()) return setError('Name is required');
    try {
      if (editing) await axios.put(`${API}/api/library/storage-areas/${editing}`, form, { headers });
      else await axios.post(`${API}/api/library/storage-areas`, form, { headers });
      setDialogOpen(false); setError(''); setSuccess(`Storage area ${editing ? 'updated' : 'created'}`); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/api/library/storage-areas/${delDlg}`, { headers }); setDelDlg(null); setSuccess('Storage area deleted'); load(); }
    catch (err) { setDelDlg(null); setError(err.response?.data?.error || 'Failed to delete'); }
  };

  if (loading) return (<Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center"><Warehouse sx={{ mr: 1, color: '#455a64' }} /><Typography variant="h5" sx={{ color: '#455a64' }}>Storage Areas</Typography></Box>
            <Box>
              <IconButton onClick={() => navigate('/library')} title="Library Dashboard" sx={{ mr: 1 }}><Home /></IconButton>
              <IconButton onClick={openNew} title="Add Storage Area"><Add /></IconButton>
            </Box>
          </Box>
          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />
          {rows.length === 0 ? <Typography color="text.secondary">No storage areas yet.</Typography> : (
            <Table size="small">
              <TableHead><TableRow><TableCell><strong>Code</strong></TableCell><TableCell><strong>Name</strong></TableCell><TableCell><strong>Description</strong></TableCell><TableCell></TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.storageCode} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.storageCode}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                      <IconButton color="warning" size="small" onClick={() => setDelDlg(r.storageCode)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(delDlg)} onClose={() => setDelDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}><WarningAmber color="warning" /> Delete Storage Area</DialogTitle>
        <DialogContent><Typography>Delete <strong>{delDlg}</strong>?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDelDlg(null)}>Cancel</Button><Button variant="contained" color="warning" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? `Edit: ${editing}` : 'New Storage Area'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Code" value={form.storageCode} disabled={!!editing} size="small" fullWidth onChange={e => setForm({ ...form, storageCode: e.target.value })} />
            <TextField label="Name" value={form.name} size="small" fullWidth onChange={e => setForm({ ...form, name: e.target.value })} />
            <TextField label="Description" value={form.description} size="small" fullWidth multiline minRows={2} onChange={e => setForm({ ...form, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={attemptClose}>Cancel</Button><Button variant="contained" onClick={save}>Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
